import React from 'react';

export interface StatButtonProps {
  label: string;
  value: string | number;
  icon?: React.ComponentType<{ className?: string }>;
  onClick?: () => void;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  selected?: boolean;
  valueColorMode?: 'default' | 'danger-if-positive';
}

export default function StatButton({
  label,
  value,
  icon: Icon,
  onClick,
  className = '',
  size = 'sm',
  selected = false,
  valueColorMode = 'default'
}: StatButtonProps) {
  const sizeClasses = {
    sm: {
      button: 'px-2 py-1 text-xs',
      icon: 'h-4 w-4'
    },
    md: {
      button: 'px-3 py-2 text-sm',
      icon: 'h-5 w-5'
    },
    lg: {
      button: 'px-4 py-2 text-base',
      icon: 'h-6 w-6'
    }
  };

  const currentSize = sizeClasses[size];

  // Label button styling based on selected state
  const labelBgClass = selected
    ? 'bg-slate-600 dark:bg-slate-700 text-white'
    : 'bg-theme-bg-primary text-heading hover:bg-theme-bg-tertiary';

  // Value button styling based on valueColorMode and value
  const numericValue = typeof value === 'number' ? value : parseInt(String(value), 10) || 0;
  const showDanger = valueColorMode === 'danger-if-positive' && numericValue > 0;
  const valueBgClass = showDanger
    ? 'bg-brand-red dark:bg-brand-red text-white'
    : 'bg-theme-bg-primary text-heading hover:bg-theme-bg-tertiary';

  return (
    <span className={`isolate inline-flex rounded-md shadow-sm ${className}`}>
      <button
        type="button"
        onClick={onClick}
        className={`relative inline-flex items-center gap-x-1 rounded-l-md ${labelBgClass} ${currentSize.button} font-semibold ring-1 ring-inset ring-theme-border-secondary focus:z-10 focus:outline-none focus:ring-2 focus:ring-indigo-600 whitespace-nowrap`}
      >
        {Icon && (
          <Icon className={`-ml-0.5 ${currentSize.icon} ${selected ? 'text-gray-200' : 'text-gray-400 dark:text-gray-500'}`} />
        )}
        {label}
      </button>
      <button
        type="button"
        onClick={onClick}
        className={`relative -ml-px inline-flex items-center rounded-r-md ${valueBgClass} ${currentSize.button} font-semibold ring-1 ring-inset ring-theme-border-secondary focus:z-10 focus:outline-none focus:ring-2 focus:ring-indigo-600 whitespace-nowrap`}
      >
        {value}
      </button>
    </span>
  );
}
